import { useState, useEffect } from 'react';
import { getISOWeek } from 'date-fns';
import { initializeDB } from '../config/db';
import { WEEK_LIST_STORE } from '../config/constants';

export const useWeeklyRoster = () => {
    const [hasCurrentWeekRoster, setHasCurrentWeekRoster] = useState(false);
    const [currentWeekEmployeeCount, setCurrentWeekEmployeeCount] = useState(0);
    const [permanentCount, setPermanentCount] = useState(0);
    const [tempCount, setTempCount] = useState(0);
    const [loading, setLoading] = useState(true);


    const checkCurrentWeekRoster = async () => {
        setLoading(true);
        try {
            const db = await initializeDB();
            const currentWeekNumber = getISOWeek(new Date());
            const currentYear = new Date().getFullYear();
            const weekYear = `${currentYear}-W${String(currentWeekNumber).padStart(2, '0')}`;
            
            const tx = db.transaction(WEEK_LIST_STORE, 'readonly');
            const store = tx.objectStore(WEEK_LIST_STORE);
            const currentList = await store.get(weekYear);
            await tx.done;

            if (currentList) {
                // Handle both new format (permanentEmployees/temporaryEmployees) and old format (employees)
                let totalCount = 0;
                let permCount = 0;
                let tmpCount = 0;
                
                if (currentList.permanentEmployees || currentList.temporaryEmployees) {
                    // New format
                    permCount = (currentList.permanentEmployees || []).length;
                    tmpCount = (currentList.temporaryEmployees || []).length;
                    totalCount = permCount + tmpCount;
                } else if (currentList.employees) {
                    // Old format - we don't know the breakdown
                    totalCount = currentList.employees.length;
                    permCount = totalCount; // Assume all are permanent for old data
                    tmpCount = 0;
                }
                
                if (totalCount > 0) {
                    setHasCurrentWeekRoster(true);
                    setCurrentWeekEmployeeCount(totalCount);
                    setPermanentCount(permCount);
                    setTempCount(tmpCount);
                } else {
                    setHasCurrentWeekRoster(false);
                    setCurrentWeekEmployeeCount(0);
                    setPermanentCount(0);
                    setTempCount(0);
                }
            } else {
                setHasCurrentWeekRoster(false);
                setCurrentWeekEmployeeCount(0);
                setPermanentCount(0);
                setTempCount(0);
            }
        } catch (error) {
            console.error('Error checking weekly roster:', error);
            setHasCurrentWeekRoster(false);
            setCurrentWeekEmployeeCount(0);
            setPermanentCount(0);
            setTempCount(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        checkCurrentWeekRoster();

        // Listen for custom event when roster is saved
        const handleRosterUpdate = (event) => {
            // Log the event details for debugging
            console.log('Roster update event received:', event.detail);
            checkCurrentWeekRoster();
        };

        window.addEventListener('weeklyRosterUpdated', handleRosterUpdate);
        
        // Check every minute in case week changes
        const interval = setInterval(checkCurrentWeekRoster, 60000);

        return () => {
            window.removeEventListener('weeklyRosterUpdated', handleRosterUpdate);
            clearInterval(interval);
        };
    }, []);

    return {
        hasCurrentWeekRoster,
        currentWeekEmployeeCount,
        permanentCount,
        tempCount,
        loading,
        refresh: checkCurrentWeekRoster
    };
};